import { useRef } from 'react';
import { Button } from './ui/button';
import { Plus, X, Loader2, Image, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const FotoUploader = ({ fotoUrls = [], setFotoUrls, maxPhotos = 10, allowPdf = true }) => {
  const fileInputRef = useRef(null);
  
  // Ensure fotoUrls is always an array
  const safeUrls = Array.isArray(fotoUrls) ? fotoUrls : [];

  const isValidFileType = (file) => {
    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf';
    return isImage || (allowPdf && isPdf);
  };

  const isPdfUrl = (url) => {
    return url?.toLowerCase().endsWith('.pdf') || url?.includes('application/pdf');
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Check max photos
    if (safeUrls.length + files.length > maxPhotos) {
      toast.error(`Puoi caricare massimo ${maxPhotos} file`);
      return;
    }

    for (const file of files) {
      // Validate file type
      if (!isValidFileType(file)) {
        toast.error(allowPdf ? 'Seleziona immagini o PDF' : 'Seleziona solo immagini');
        continue;
      }

      // Validate file size (max 10MB for PDF, 5MB for images)
      const maxSize = file.type === 'application/pdf' ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(`File troppo grande (max ${file.type === 'application/pdf' ? '10MB' : '5MB'})`);
        continue;
      }

      try {
        // Convert to base64
        const base64 = await fileToBase64(file);
        
        // Upload to backend
        const token = localStorage.getItem('vault_token');
        const response = await fetch(`${BACKEND_URL}/api/upload`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            filename: file.name,
            content_type: file.type,
            data: base64
          })
        });

        if (!response.ok) {
          throw new Error('Upload fallito');
        }

        const result = await response.json();
        setFotoUrls(prev => [...prev, result.url]);
        toast.success(file.type === 'application/pdf' ? 'PDF caricato' : 'Foto caricata');
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento del file');
      }
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove data URL prefix
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const removeFoto = (url) => {
    setFotoUrls(prev => prev.filter(u => u !== url));
  };

  const acceptTypes = allowPdf ? "image/*,.pdf,application/pdf" : "image/*";

  return (
    <div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept={acceptTypes}
        multiple
        className="hidden"
      />
      
      <div className="flex gap-2 mb-3">
        <Button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex-1 h-11 bg-[#262626] hover:bg-[#363636] text-white rounded-sm"
          disabled={safeUrls.length >= maxPhotos}
        >
          <Plus className="w-4 h-4 mr-2" />
          {allowPdf ? 'Carica Foto/PDF' : 'Carica Foto'}
        </Button>
      </div>

      {safeUrls.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {safeUrls.map((url, idx) => (
            <div key={idx} className="relative group aspect-square">
              {isPdfUrl(url) ? (
                <a 
                  href={url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-full h-full flex flex-col items-center justify-center bg-[#1a1a1a] border border-[#262626] hover:border-[#FF5F1F] transition-colors"
                >
                  <FileText className="w-8 h-8 text-[#FF5F1F] mb-1" />
                  <span className="text-[#9CA3AF] text-xs">PDF</span>
                </a>
              ) : (
                <img 
                  src={url} 
                  alt="" 
                  className="w-full h-full object-cover border border-[#262626]" 
                />
              )}
              <button
                type="button"
                onClick={() => removeFoto(url)}
                className="absolute top-1 right-1 bg-red-500 p-1 opacity-0 group-hover:opacity-100 transition-opacity rounded-sm"
              >
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}

      {safeUrls.length === 0 && (
        <div 
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-[#262626] p-6 text-center cursor-pointer hover:border-[#FF5F1F]/50 transition-colors"
        >
          <div className="flex justify-center gap-2 mb-2">
            <Image className="w-8 h-8 text-[#4B5563]" />
            {allowPdf && <FileText className="w-8 h-8 text-[#4B5563]" />}
          </div>
          <p className="text-[#9CA3AF] text-sm">
            {allowPdf ? 'Clicca per caricare foto o PDF' : 'Clicca per caricare foto'}
          </p>
          <p className="text-[#4B5563] text-xs mt-1">
            {allowPdf ? 'JPG, PNG, PDF (max 10MB)' : 'JPG, PNG (max 5MB)'}
          </p>
        </div>
      )}
    </div>
  );
};
